import React, { useEffect, useMemo, useState } from "react";
import { calls as callsApi, hotels, shoppers as shoppersApi } from "../api/endpoints";
import { DataTableShell, EmptyState, MetricStrip, PageHeader, SectionCard } from "../components/Workspace";
import type { CallImportSummary, CallType, HotelProperty, ServiceLevel, User } from "../types";

const EXPECTED_COLUMNS = [
  "HotelCode",
  "HotelName",
  "SalesStaffName",
  "SalesStaffEmail",
  "ShoppersEmail",
  "CallType",
  "ServiceLevel",
  "Scenario",
  "ScheduledMonth",
  "ScheduledDate",
  "Earliest",
  "HMC",
  "ContractYear",
  "OrderID",
  "InvoiceID",
  "Language",
];

function statusTone(status: string) {
  if (status === "imported") return "tone-success";
  if (status === "ready") return "tone-brand";
  return "tone-danger";
}

function StatusPill({ status }: { status: string }) {
  return (
    <span className={`badge-pill ${statusTone(status)}`.trim()}>
      {status === "ready" ? "Ready" : status === "imported" ? "Imported" : "Needs review"}
    </span>
  );
}

function serviceLevelLabel(serviceLevel: ServiceLevel) {
  return [serviceLevel.name, serviceLevel.description || serviceLevel.abbreviation]
    .filter(Boolean)
    .join(" - ");
}

type BatchDistribution = "round_robin" | "all_shoppers";
type SummarySource = "file" | "batch" | "manual" | null;
type BatchSalesStaffContact = { name: string; email: string };

function isoMonth(value: string) {
  if (!value) return "";
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? "" : String(parsed.getMonth() + 1);
}

function isoYear(value: string) {
  if (!value) return "";
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? "" : String(parsed.getFullYear());
}

function clampRepeatCount(value: string | number) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 1;
  return Math.max(1, Math.min(12, Math.floor(parsed)));
}

function clampSalesStaffCount(value: string | number) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 1;
  return Math.max(1, Math.min(12, Math.floor(parsed)));
}

export default function ImportedCallsPage() {
  const [file, setFile] = useState<File | null>(null);
  const [summary, setSummary] = useState<CallImportSummary | null>(null);
  const [summarySource, setSummarySource] = useState<SummarySource>(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const [lookupsLoading, setLookupsLoading] = useState(false);
  const [properties, setProperties] = useState<HotelProperty[]>([]);
  const [callTypes, setCallTypes] = useState<CallType[]>([]);
  const [serviceLevels, setServiceLevels] = useState<ServiceLevel[]>([]);
  const [shoppers, setShoppers] = useState<User[]>([]);
  const [batchFile, setBatchFile] = useState<File | null>(null);
  const [lastBatchSignature, setLastBatchSignature] = useState("");
  const [batchForm, setBatchForm] = useState<{
    hotelSearch: string;
    hmcFilter: string;
    selectedPropertyIds: number[];
    selectedShopperEmails: string[];
    distribution: BatchDistribution;
    sales_staff_name: string;
    sales_staff_email: string;
    call_type: string;
    service_level: string;
    scenario: string;
    scheduled_date: string;
    earliest: string;
    invoice_id: string;
    order_id: string;
    language: string;
    contract_year: string;
    repeat_count: number;
    repeat_dates: string[];
    sales_staff_count: number;
    additional_sales_staff: BatchSalesStaffContact[];
  }>({
    hotelSearch: "",
    hmcFilter: "",
    selectedPropertyIds: [],
    selectedShopperEmails: [],
    distribution: "round_robin",
    sales_staff_name: "Guest Services",
    sales_staff_email: "reservation@shepherdandassoc.com",
    call_type: "",
    service_level: "",
    scenario: "",
    scheduled_date: "",
    earliest: "",
    invoice_id: "",
    order_id: "",
    language: "",
    contract_year: String(new Date().getFullYear()),
    repeat_count: 1,
    repeat_dates: [],
    sales_staff_count: 1,
    additional_sales_staff: [],
  });
  const [manualForm, setManualForm] = useState({
    property_id: 0,
    sales_staff_name: "",
    sales_staff_email: "",
    shopper_email: "",
    call_type: "",
    service_level: "",
    scenario: "",
    scheduled_date: "",
    earliest: "",
    invoice_id: "",
    order_id: "",
    language: "",
  });
  const [manualHotelSearch, setManualHotelSearch] = useState("");
  const [notice, setNotice] = useState<{ tone: "success" | "danger"; message: string } | null>(null);

  async function loadLookupData(cancelledRef?: { current: boolean }) {
    setLookupsLoading(true);
    const [propertyRows, typeRows, serviceRows, shopperRows] = await Promise.allSettled([
      hotels.properties.list("limit=5000"),
      callsApi.types(),
      callsApi.serviceLevels(),
      shoppersApi.list("limit=5000"),
    ]);
    if (cancelledRef?.current) return;

    const failures: string[] = [];
    if (propertyRows.status === "fulfilled") {
      setProperties(propertyRows.value.items);
    } else {
      failures.push("hotels");
    }
    if (typeRows.status === "fulfilled") {
      setCallTypes(typeRows.value);
    } else {
      failures.push("call types");
    }
    if (serviceRows.status === "fulfilled") {
      setServiceLevels(serviceRows.value);
    } else {
      failures.push("service levels");
    }
    if (shopperRows.status === "fulfilled") {
      setShoppers(shopperRows.value.items);
    } else {
      failures.push("shoppers");
    }
    if (failures.length > 0) {
      setNotice({
        tone: "danger",
        message: `Some dropdowns did not load: ${failures.join(", ")}. Refresh the lists or sign in again if the session expired.`,
      });
    }
    setLookupsLoading(false);
  }

  useEffect(() => {
    const cancelledRef = { current: false };
    loadLookupData(cancelledRef);
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const metricItems = useMemo(() => {
    if (!summary) {
      return [
        { label: "Rows", value: 0, detail: "Upload a file to validate", tone: "neutral" as const },
        { label: "Ready", value: 0, detail: "Rows that can import cleanly", tone: "brand" as const },
        { label: "Warnings", value: 0, detail: "Rows with non-blocking notes", tone: "warning" as const },
        { label: "Issues", value: 0, detail: "Rows that need changes", tone: "danger" as const },
      ];
    }
    return [
      { label: "Rows", value: summary.total_rows, detail: summary.file_name, tone: "neutral" as const },
      { label: "Ready", value: summary.ready_rows, detail: "Can be imported now", tone: "brand" as const },
      { label: "Warnings", value: summary.warning_rows, detail: "Will still import", tone: "warning" as const },
      {
        label: importing || summary.imported_rows > 0 ? "Imported" : "Issues",
        value: importing || summary.imported_rows > 0 ? summary.imported_rows : summary.invalid_rows,
        detail: importing || summary.imported_rows > 0 ? "Rows created in calls" : "Rows blocked by validation",
        tone: importing || summary.imported_rows > 0 ? ("success" as const) : ("danger" as const),
      },
    ];
  }, [summary, importing]);

  const canImport = Boolean(file && summarySource === "file" && summary && summary.ready_rows > 0 && !loading && !importing);
  const manualLookupsReady = properties.length > 0 && callTypes.length > 0 && serviceLevels.length > 0 && shoppers.length > 0;

  const managementCompanyOptions = useMemo(() => {
    const options = new Map<number, string>();
    properties.forEach((property) => {
      if (property.management_company) {
        options.set(property.management_company.id, property.management_company.name);
      }
    });
    return Array.from(options.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [properties]);

  const filteredBatchProperties = useMemo(() => {
    const search = batchForm.hotelSearch.trim().toLowerCase();
    const hmcId = Number(batchForm.hmcFilter);
    return properties.filter((property) => {
      if (hmcId && property.management_company?.id !== hmcId) return false;
      if (!search) return true;
      return [
        property.hotel_code,
        property.name,
        property.city,
        property.state,
        property.management_company?.name || "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(search);
    });
  }, [batchForm.hmcFilter, batchForm.hotelSearch, properties]);

  const visibleBatchProperties = filteredBatchProperties.slice(0, 160);
  const selectedBatchProperties = useMemo(
    () => properties.filter((property) => batchForm.selectedPropertyIds.includes(property.id)),
    [batchForm.selectedPropertyIds, properties]
  );
  const selectedVisibleBatchCount = visibleBatchProperties.filter((property) =>
    batchForm.selectedPropertyIds.includes(property.id)
  ).length;
  const selectedBatchShoppers = useMemo(
    () => shoppers.filter((shopper) => batchForm.selectedShopperEmails.includes(shopper.email)),
    [batchForm.selectedShopperEmails, shoppers]
  );
  const selectedManualProperty = useMemo(
    () => properties.find((property) => property.id === manualForm.property_id) || null,
    [manualForm.property_id, properties]
  );
  const visibleManualProperties = useMemo(() => {
    const search = manualHotelSearch.trim().toLowerCase();
    if (!search) {
      return properties.slice(0, 20);
    }
    return properties
      .filter((property) =>
        [
          property.hotel_code,
          property.name,
          property.city,
          property.state,
          property.management_company?.name || "",
        ]
          .join(" ")
          .toLowerCase()
          .includes(search)
      )
      .slice(0, 30);
  }, [manualHotelSearch, properties]);
  const batchRepeatCount = clampRepeatCount(batchForm.repeat_count);
  const batchScheduledDates = useMemo(
    () => [batchForm.scheduled_date, ...batchForm.repeat_dates].slice(0, batchRepeatCount),
    [batchForm.repeat_dates, batchForm.scheduled_date, batchRepeatCount]
  );
  const batchSalesStaffCount = clampSalesStaffCount(batchForm.sales_staff_count);
  const batchSalesStaffContacts = useMemo(
    () =>
      [
        { name: batchForm.sales_staff_name, email: batchForm.sales_staff_email },
        ...batchForm.additional_sales_staff,
      ].slice(0, batchSalesStaffCount),
    [batchForm.additional_sales_staff, batchForm.sales_staff_email, batchForm.sales_staff_name, batchSalesStaffCount]
  );
  const batchGeneratedRowCount =
    selectedBatchProperties.length *
    (batchForm.distribution === "all_shoppers" ? Math.max(selectedBatchShoppers.length, 0) : selectedBatchShoppers.length > 0 ? 1 : 0) *
    batchRepeatCount *
    batchSalesStaffCount;
  const batchSignature = JSON.stringify({
    selectedPropertyIds: batchForm.selectedPropertyIds,
    selectedShopperEmails: batchForm.selectedShopperEmails,
    distribution: batchForm.distribution,
    sales_staff_name: batchForm.sales_staff_name,
    sales_staff_email: batchForm.sales_staff_email,
    call_type: batchForm.call_type,
    service_level: batchForm.service_level,
    scenario: batchForm.scenario,
    scheduled_date: batchForm.scheduled_date,
    earliest: batchForm.earliest,
    invoice_id: batchForm.invoice_id,
    order_id: batchForm.order_id,
    language: batchForm.language,
    contract_year: batchForm.contract_year,
    repeat_count: batchRepeatCount,
    scheduled_dates: batchScheduledDates,
    sales_staff_count: batchSalesStaffCount,
    sales_staff_contacts: batchSalesStaffContacts,
  });
  const canImportBatch = Boolean(
    batchFile &&
    summarySource === "batch" &&
    lastBatchSignature === batchSignature &&
    summary &&
    summary.ready_rows > 0 &&
    !loading &&
    !importing
  );

  async function handlePreview() {
    if (!file) {
      setNotice({ tone: "danger", message: "Choose a .csv, .xls, or .xlsx file first." });
      return;
    }
    setLoading(true);
    setNotice(null);
    try {
      const result = await callsApi.importPreview(file);
      setSummary(result);
      setSummarySource("file");
      setNotice({ tone: "success", message: result.message });
    } catch (error) {
      setSummary(null);
      setSummarySource(null);
      setNotice({ tone: "danger", message: error instanceof Error ? error.message : "Preview failed." });
    } finally {
      setLoading(false);
    }
  }

  async function handleImport() {
    if (!file) return;
    setImporting(true);
    setNotice(null);
    try {
      const result = await callsApi.importCommit(file);
      setSummary(result);
      setSummarySource("file");
      setNotice({
        tone: result.failed_rows > 0 ? "danger" : "success",
        message: result.message,
      });
    } catch (error) {
      setNotice({ tone: "danger", message: error instanceof Error ? error.message : "Import failed." });
    } finally {
      setImporting(false);
    }
  }

  function csvCell(value: string | number | null | undefined) {
    const text = String(value ?? "");
    return `"${text.replace(/"/g, '""')}"`;
  }

  function updateBatchForm(patch: Partial<typeof batchForm>) {
    setBatchForm((current) => ({ ...current, ...patch }));
  }

  function updateBatchRepeatCount(value: string) {
    const repeat_count = clampRepeatCount(value);
    setBatchForm((current) => ({
      ...current,
      repeat_count,
      repeat_dates: current.repeat_dates.slice(0, Math.max(repeat_count - 1, 0)),
    }));
  }

  function updateBatchRepeatDate(index: number, value: string) {
    setBatchForm((current) => {
      const repeat_dates = [...current.repeat_dates];
      repeat_dates[index - 1] = value;
      return { ...current, repeat_dates };
    });
  }

  function updateBatchSalesStaffCount(value: string) {
    const sales_staff_count = clampSalesStaffCount(value);
    setBatchForm((current) => ({
      ...current,
      sales_staff_count,
      additional_sales_staff: current.additional_sales_staff.slice(0, Math.max(sales_staff_count - 1, 0)),
    }));
  }

  function updateAdditionalSalesStaff(index: number, patch: Partial<BatchSalesStaffContact>) {
    setBatchForm((current) => {
      const additional_sales_staff = [...current.additional_sales_staff];
      additional_sales_staff[index] = {
        name: additional_sales_staff[index]?.name || "",
        email: additional_sales_staff[index]?.email || "",
        ...patch,
      };
      return { ...current, additional_sales_staff };
    });
  }

  function toggleBatchProperty(propertyId: number) {
    setBatchForm((current) => ({
      ...current,
      selectedPropertyIds: current.selectedPropertyIds.includes(propertyId)
        ? current.selectedPropertyIds.filter((id) => id !== propertyId)
        : [...current.selectedPropertyIds, propertyId],
    }));
  }

  function toggleBatchShopper(email: string) {
    setBatchForm((current) => ({
      ...current,
      selectedShopperEmails: current.selectedShopperEmails.includes(email)
        ? current.selectedShopperEmails.filter((item) => item !== email)
        : [...current.selectedShopperEmails, email],
    }));
  }

  function selectManualProperty(property: HotelProperty) {
    setManualForm((current) => ({
      ...current,
      property_id: property.id,
      sales_staff_name: current.sales_staff_name || property.dos_name || "",
      sales_staff_email: current.sales_staff_email || property.dos_email || "",
    }));
    setManualHotelSearch(`${property.hotel_code} - ${property.name}`);
  }

  function buildBatchCsvFile() {
    const rows: string[] = [];

    selectedBatchProperties.forEach((property, propertyIndex) => {
      batchScheduledDates.forEach((scheduledDate, dateIndex) => {
        const assignedShoppers =
          batchForm.distribution === "all_shoppers"
            ? selectedBatchShoppers
            : [selectedBatchShoppers[(propertyIndex * batchRepeatCount + dateIndex) % selectedBatchShoppers.length]].filter(Boolean);

        assignedShoppers.forEach((shopper) => {
          batchSalesStaffContacts.forEach((salesStaff) => {
            rows.push([
              property.hotel_code,
              property.name,
              salesStaff.name || property.dos_name,
              salesStaff.email || property.dos_email,
              shopper.email,
              batchForm.call_type,
              batchForm.service_level,
              batchForm.scenario,
              isoMonth(scheduledDate),
              scheduledDate,
              batchForm.earliest,
              property.management_company?.name || "",
              batchForm.contract_year || isoYear(scheduledDate),
              batchForm.order_id,
              batchForm.invoice_id,
              batchForm.language,
            ].map(csvCell).join(","));
          });
        });
      });
    });

    const content = `${EXPECTED_COLUMNS.join(",")}\n${rows.join("\n")}\n`;
    const safeDate = batchForm.scheduled_date || "unscheduled";
    return new File([content], `batch-shop-calls-${safeDate}.csv`, { type: "text/csv" });
  }

  function validateBatchForm() {
    if (!manualLookupsReady) return "The dropdown lists are still loading. Refresh the lists, then try again.";
    if (selectedBatchProperties.length === 0) return "Choose at least one hotel for the batch.";
    if (selectedBatchShoppers.length === 0) return "Choose at least one shopper for the batch.";
    if (!batchForm.scheduled_date) return "Scheduled date is required for generated calls.";
    if (batchRepeatCount > 1 && batchScheduledDates.some((date) => !date)) return "Add a scheduled date for each repeated shop call.";
    const uniqueScheduledDates = new Set(batchScheduledDates.filter(Boolean));
    if (uniqueScheduledDates.size !== batchScheduledDates.length) return "Each repeated shop call needs a different scheduled date.";
    if (batchSalesStaffCount > 1 && batchSalesStaffContacts.some((contact) => !contact.name.trim() && !contact.email.trim())) {
      return "Add a salesperson name or email for each salesperson.";
    }
    const uniqueSalesStaff = new Set(batchSalesStaffContacts.map((contact) => `${contact.name.trim().toLowerCase()}|${contact.email.trim().toLowerCase()}`));
    if (uniqueSalesStaff.size !== batchSalesStaffContacts.length) return "Each salesperson row needs to be unique.";
    if (!batchForm.call_type) return "Call type is required for generated calls.";
    if (!batchForm.service_level) return "Service level is required for generated calls.";
    if (batchGeneratedRowCount > 500) return "This batch is over 500 rows. Split it into smaller batches before importing.";
    return "";
  }

  async function handleBatchPreview() {
    const validationMessage = validateBatchForm();
    if (validationMessage) {
      setNotice({ tone: "danger", message: validationMessage });
      return;
    }
    const generatedFile = buildBatchCsvFile();
    setBatchFile(generatedFile);
    setLastBatchSignature(batchSignature);
    setLoading(true);
    setNotice(null);
    try {
      const result = await callsApi.importPreview(generatedFile);
      setSummary(result);
      setSummarySource("batch");
      setNotice({ tone: "success", message: `Generated ${batchGeneratedRowCount} rows. ${result.message}` });
    } catch (error) {
      setSummary(null);
      setSummarySource(null);
      setNotice({ tone: "danger", message: error instanceof Error ? error.message : "Batch validation failed." });
    } finally {
      setLoading(false);
    }
  }

  async function handleBatchImport() {
    if (!batchFile || lastBatchSignature !== batchSignature) {
      setNotice({ tone: "danger", message: "Preview the current batch before importing it." });
      return;
    }
    setImporting(true);
    setNotice(null);
    try {
      const result = await callsApi.importCommit(batchFile);
      setSummary(result);
      setSummarySource("batch");
      setNotice({
        tone: result.failed_rows > 0 ? "danger" : "success",
        message: result.message,
      });
    } catch (error) {
      setNotice({ tone: "danger", message: error instanceof Error ? error.message : "Batch import failed." });
    } finally {
      setImporting(false);
    }
  }

  async function handleManualSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!manualLookupsReady) {
      setNotice({ tone: "danger", message: "The dropdown lists are still empty. Refresh the lists, then save again." });
      return;
    }
    const property = properties.find((item) => item.id === manualForm.property_id);
    if (!property) {
      setNotice({ tone: "danger", message: "Choose a hotel property before saving the manual call." });
      return;
    }
    setManualSaving(true);
    setNotice(null);
    try {
      const header = EXPECTED_COLUMNS.join(",");
      const row = [
        property.hotel_code,
        property.name,
        manualForm.sales_staff_name,
        manualForm.sales_staff_email,
        manualForm.shopper_email,
        manualForm.call_type,
        manualForm.service_level,
        manualForm.scenario,
        manualForm.scheduled_date ? String(new Date(`${manualForm.scheduled_date}T00:00:00`).getMonth() + 1) : "",
        manualForm.scheduled_date,
        manualForm.earliest,
        property.management_company?.name || "",
        manualForm.scheduled_date ? String(new Date(`${manualForm.scheduled_date}T00:00:00`).getFullYear()) : "",
        manualForm.order_id,
        manualForm.invoice_id,
        manualForm.language,
      ].map(csvCell).join(",");
      const manualFile = new File([`${header}\n${row}\n`], "manual-shop-call.csv", { type: "text/csv" });
      const result = await callsApi.importCommit(manualFile);
      setSummary(result);
      setSummarySource("manual");
      if (result.failed_rows > 0) {
        setNotice({ tone: "danger", message: "The manual call was checked, but it needs review before it can be saved." });
      } else {
        setNotice({ tone: "success", message: "Manual shop call saved." });
        setManualForm({
          property_id: 0,
          sales_staff_name: "",
          sales_staff_email: "",
          shopper_email: "",
          call_type: "",
          service_level: "",
          scenario: "",
          scheduled_date: "",
          earliest: "",
          invoice_id: "",
          order_id: "",
          language: "",
        });
      }
    } catch (error) {
      setNotice({ tone: "danger", message: error instanceof Error ? error.message : "Manual call save failed." });
    } finally {
      setManualSaving(false);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Imported Calls"
        title="Import Call Details"
        description="Upload a call-details spreadsheet, validate every row, and import only the rows that resolve cleanly."
        compact
      />

      <MetricStrip items={metricItems} />
      {notice ? (
        <div
          className={`rounded-2xl border px-4 py-3 text-sm ${
            notice.tone === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-rose-200 bg-rose-50 text-rose-800"
          }`.trim()}
        >
          {notice.message}
        </div>
      ) : null}

      <SectionCard
        title="Batch Schedule Builder"
        subtitle="Create many scheduled shop calls from shared details, then validate the generated rows before importing."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <span className="badge-pill tone-neutral">{batchGeneratedRowCount} generated rows</span>
            <button type="button" onClick={handleBatchPreview} className="button-secondary" disabled={loading || importing}>
              {loading ? "Validating..." : "Preview batch"}
            </button>
            <button type="button" onClick={handleBatchImport} className="button-primary" disabled={!canImportBatch}>
              {importing ? "Importing..." : "Import batch"}
            </button>
          </div>
        }
      >
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1.1fr),minmax(22rem,0.9fr)]">
          <div className="space-y-4">
            <div className="grid gap-4 lg:grid-cols-4">
              <div>
                <label className="label-caps mb-1.5 block">Scheduled Date</label>
                <input
                  type="date"
                  value={batchForm.scheduled_date}
                  onChange={(event) => updateBatchForm({ scheduled_date: event.target.value, contract_year: isoYear(event.target.value) })}
                  className="input-shell w-full"
                />
              </div>
              <div>
                <label className="label-caps mb-1.5 block">Shop Calls</label>
                <input
                  type="number"
                  min={1}
                  max={12}
                  value={batchRepeatCount}
                  onChange={(event) => updateBatchRepeatCount(event.target.value)}
                  className="input-shell w-full"
                />
              </div>
              <div>
                <label className="label-caps mb-1.5 block">Call Type</label>
                <select
                  value={batchForm.call_type}
                  onChange={(event) => updateBatchForm({ call_type: event.target.value })}
                  className="select-shell w-full"
                >
                  <option value="">{lookupsLoading ? "Loading call types..." : "Select type"}</option>
                  {callTypes.map((callType) => (
                    <option key={callType.id} value={callType.name}>{callType.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-caps mb-1.5 block">Service Level</label>
                <select
                  value={batchForm.service_level}
                  onChange={(event) => updateBatchForm({ service_level: event.target.value })}
                  className="select-shell w-full"
                >
                  <option value="">{lookupsLoading ? "Loading service levels..." : "Select service"}</option>
                  {serviceLevels.map((serviceLevel) => {
                    const label = serviceLevelLabel(serviceLevel);
                    return <option key={serviceLevel.id} value={label}>{label}</option>;
                  })}
                </select>
              </div>
              <div>
                <label className="label-caps mb-1.5 block">Salesperson 1 Name</label>
                <input
                  value={batchForm.sales_staff_name}
                  onChange={(event) => updateBatchForm({ sales_staff_name: event.target.value })}
                  className="input-shell w-full"
                  placeholder="Guest Services"
                />
              </div>
              <div>
                <label className="label-caps mb-1.5 block">Salesperson 1 Email</label>
                <input
                  type="email"
                  value={batchForm.sales_staff_email}
                  onChange={(event) => updateBatchForm({ sales_staff_email: event.target.value })}
                  className="input-shell w-full"
                  placeholder="reservation@shepherdandassoc.com"
                />
              </div>
              <div>
                <label className="label-caps mb-1.5 block">Salespeople</label>
                <input
                  type="number"
                  min={1}
                  max={12}
                  value={batchSalesStaffCount}
                  onChange={(event) => updateBatchSalesStaffCount(event.target.value)}
                  className="input-shell w-full"
                />
              </div>
              <div>
                <label className="label-caps mb-1.5 block">Assignment</label>
                <select
                  value={batchForm.distribution}
                  onChange={(event) => updateBatchForm({ distribution: event.target.value as BatchDistribution })}
                  className="select-shell w-full"
                >
                  <option value="round_robin">Rotate selected shoppers across calls</option>
                  <option value="all_shoppers">Create every hotel for every shopper</option>
                </select>
                <p className="mt-1 text-xs text-slate-500">
                  To keep every repeat with one person, select only that shopper.
                </p>
              </div>
              <div>
                <label className="label-caps mb-1.5 block">Earliest Date</label>
                <input
                  type="date"
                  value={batchForm.earliest}
                  onChange={(event) => updateBatchForm({ earliest: event.target.value })}
                  className="input-shell w-full"
                />
              </div>
              <div>
                <label className="label-caps mb-1.5 block">Invoice ID</label>
                <input
                  value={batchForm.invoice_id}
                  onChange={(event) => updateBatchForm({ invoice_id: event.target.value })}
                  className="input-shell w-full"
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="label-caps mb-1.5 block">Contract Year</label>
                <input
                  value={batchForm.contract_year}
                  onChange={(event) => updateBatchForm({ contract_year: event.target.value })}
                  className="input-shell w-full"
                  placeholder="2026"
                />
              </div>
              <div>
                <label className="label-caps mb-1.5 block">Order ID</label>
                <input
                  value={batchForm.order_id}
                  onChange={(event) => updateBatchForm({ order_id: event.target.value })}
                  className="input-shell w-full"
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="label-caps mb-1.5 block">Language</label>
                <input
                  value={batchForm.language}
                  onChange={(event) => updateBatchForm({ language: event.target.value })}
                  className="input-shell w-full"
                  placeholder="Optional"
                />
              </div>
            </div>
            {batchRepeatCount > 1 ? (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {Array.from({ length: batchRepeatCount - 1 }, (_, index) => {
                  const dateIndex = index + 2;
                  return (
                    <div key={dateIndex}>
                      <label className="label-caps mb-1.5 block">Date {dateIndex}</label>
                      <input
                        type="date"
                        value={batchForm.repeat_dates[index] || ""}
                        onChange={(event) => updateBatchRepeatDate(dateIndex - 1, event.target.value)}
                        className="input-shell w-full"
                      />
                    </div>
                  );
                })}
              </div>
            ) : null}
            {batchSalesStaffCount > 1 ? (
              <div className="grid gap-3 lg:grid-cols-2">
                {Array.from({ length: batchSalesStaffCount - 1 }, (_, index) => {
                  const contact = batchForm.additional_sales_staff[index] || { name: "", email: "" };
                  const label = index + 2;
                  return (
                    <div key={label} className="grid gap-3 rounded-[18px] border border-slate-200 bg-slate-50/60 p-3 sm:grid-cols-2">
                      <div>
                        <label className="label-caps mb-1.5 block">Salesperson {label} Name</label>
                        <input
                          value={contact.name}
                          onChange={(event) => updateAdditionalSalesStaff(index, { name: event.target.value })}
                          className="input-shell w-full"
                          placeholder="Salesperson name"
                        />
                      </div>
                      <div>
                        <label className="label-caps mb-1.5 block">Salesperson {label} Email</label>
                        <input
                          type="email"
                          value={contact.email}
                          onChange={(event) => updateAdditionalSalesStaff(index, { email: event.target.value })}
                          className="input-shell w-full"
                          placeholder="sales@hotel.com"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
            <div>
              <label className="label-caps mb-1.5 block">Scenario</label>
              <textarea
                value={batchForm.scenario}
                onChange={(event) => updateBatchForm({ scenario: event.target.value })}
                className="input-shell min-h-[112px] w-full"
                placeholder="Add the shared scenario for this batch."
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="label-caps mb-1.5 block">HMC Filter</label>
                <select
                  value={batchForm.hmcFilter}
                  onChange={(event) => updateBatchForm({ hmcFilter: event.target.value })}
                  className="select-shell w-full"
                >
                  <option value="">All HMCs</option>
                  {managementCompanyOptions.map((company) => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-caps mb-1.5 block">Find Hotels</label>
                <input
                  value={batchForm.hotelSearch}
                  onChange={(event) => updateBatchForm({ hotelSearch: event.target.value })}
                  className="input-shell w-full"
                  placeholder="Code, hotel, city, state"
                />
              </div>
            </div>

            <div className="rounded-[18px] border border-slate-200 bg-slate-50/60 p-3">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Hotels</div>
                  <div className="text-xs text-slate-500">
                    {batchForm.selectedPropertyIds.length} selected, {selectedVisibleBatchCount} checked in this list, showing {visibleBatchProperties.length} of {filteredBatchProperties.length}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="button-secondary !px-3 !py-2 !text-xs"
                    onClick={() => {
                      const visibleIds = visibleBatchProperties.map((property) => property.id);
                      updateBatchForm({ selectedPropertyIds: Array.from(new Set([...batchForm.selectedPropertyIds, ...visibleIds])) });
                    }}
                  >
                    Select all shown
                  </button>
                  <button
                    type="button"
                    className="button-secondary !px-3 !py-2 !text-xs"
                    onClick={() => updateBatchForm({ selectedPropertyIds: [] })}
                  >
                    Clear
                  </button>
                </div>
              </div>
              <p className="mb-3 text-xs text-slate-500">
                To schedule one hotel, check only that hotel. Use Select all shown only when every hotel currently listed should be included.
              </p>
              <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                {visibleBatchProperties.map((property) => (
                  <label key={property.id} className="flex cursor-pointer items-start gap-3 rounded-xl bg-white px-3 py-2 text-sm shadow-sm ring-1 ring-slate-200/80">
                    <input
                      type="checkbox"
                      checked={batchForm.selectedPropertyIds.includes(property.id)}
                      onChange={() => toggleBatchProperty(property.id)}
                      className="mt-1"
                    />
                    <span className="min-w-0">
                      <span className="block font-semibold text-slate-900">{property.hotel_code} - {property.name}</span>
                      <span className="block text-xs text-slate-500">
                        {[property.city, property.state, property.management_company?.name].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                  </label>
                ))}
                {visibleBatchProperties.length === 0 ? (
                  <div className="rounded-xl bg-white px-3 py-4 text-sm text-slate-500">No hotels match the current filters.</div>
                ) : null}
              </div>
            </div>

            <div className="rounded-[18px] border border-slate-200 bg-slate-50/60 p-3">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Shoppers</div>
                  <div className="text-xs text-slate-500">
                    {batchForm.selectedShopperEmails.length} selected
                  </div>
                </div>
                <button
                  type="button"
                  className="button-secondary !px-3 !py-2 !text-xs"
                  onClick={() => updateBatchForm({ selectedShopperEmails: [] })}
                >
                  Clear shoppers
                </button>
              </div>
              <p className="mb-3 text-xs text-slate-500">
                Rotation uses every checked shopper. Clear the list before choosing one shopper for repeated calls.
              </p>
              <div className="grid max-h-52 gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
                {shoppers.map((shopper) => {
                  const name = `${shopper.first_name} ${shopper.last_name}`.trim() || shopper.email;
                  return (
                    <label key={shopper.id} className="flex cursor-pointer items-start gap-3 rounded-xl bg-white px-3 py-2 text-sm shadow-sm ring-1 ring-slate-200/80">
                      <input
                        type="checkbox"
                        checked={batchForm.selectedShopperEmails.includes(shopper.email)}
                        onChange={() => toggleBatchShopper(shopper.email)}
                        className="mt-1"
                      />
                      <span className="min-w-0">
                        <span className="block font-semibold text-slate-900">{name}</span>
                        <span className="block truncate text-xs text-slate-500">{shopper.email}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="Enter One Shop Call"
        subtitle={
          lookupsLoading
            ? "Loading hotel, shopper, type, and service lists."
            : "Add a single shop call here when there is no spreadsheet to upload."
        }
        actions={
          <button type="button" onClick={() => loadLookupData()} className="button-secondary" disabled={lookupsLoading}>
            {lookupsLoading ? "Refreshing..." : "Refresh lists"}
          </button>
        }
      >
        <form onSubmit={handleManualSubmit} className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-3">
            <label className="label-caps mb-1.5 block">Hotel Property</label>
            <input
              value={manualHotelSearch}
              onChange={(event) => {
                setManualHotelSearch(event.target.value);
                setManualForm((current) => ({ ...current, property_id: 0 }));
              }}
              className="input-shell w-full"
              placeholder={lookupsLoading ? "Loading hotels..." : "Start typing hotel code, name, city, state, or HMC"}
              required={!manualForm.property_id}
            />
            {selectedManualProperty ? (
              <div className="mt-2 flex flex-wrap items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-sm text-emerald-800 ring-1 ring-emerald-200">
                <span className="font-semibold">
                  Selected: {selectedManualProperty.hotel_code} - {selectedManualProperty.name}
                </span>
                <button
                  type="button"
                  className="text-xs font-semibold text-emerald-900 underline"
                  onClick={() => {
                    setManualForm((current) => ({ ...current, property_id: 0 }));
                    setManualHotelSearch("");
                  }}
                >
                  Change
                </button>
              </div>
            ) : (
              <div className="mt-2 max-h-64 overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
                {visibleManualProperties.map((property) => (
                  <button
                    key={property.id}
                    type="button"
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                    onClick={() => selectManualProperty(property)}
                  >
                    <span className="block font-semibold text-slate-900">{property.hotel_code} - {property.name}</span>
                    <span className="block text-xs text-slate-500">
                      {[property.city, property.state, property.management_company?.name].filter(Boolean).join(" · ")}
                    </span>
                  </button>
                ))}
                {!lookupsLoading && visibleManualProperties.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-slate-500">No hotels match that search.</div>
                ) : null}
              </div>
            )}
          </div>
          <div>
            <label className="label-caps mb-1.5 block">Sales Staff Name</label>
            <input
              value={manualForm.sales_staff_name}
              onChange={(event) => setManualForm({ ...manualForm, sales_staff_name: event.target.value })}
              className="input-shell w-full"
              placeholder="Salesperson name"
            />
          </div>
          <div>
            <label className="label-caps mb-1.5 block">Sales Staff Email</label>
            <input
              type="email"
              value={manualForm.sales_staff_email}
              onChange={(event) => setManualForm({ ...manualForm, sales_staff_email: event.target.value })}
              className="input-shell w-full"
              placeholder="sales@hotel.com"
            />
          </div>
          <div>
            <label className="label-caps mb-1.5 block">Shopper / Coach</label>
            <select
              value={manualForm.shopper_email}
              onChange={(event) => setManualForm({ ...manualForm, shopper_email: event.target.value })}
              className="select-shell w-full"
              required
            >
              <option value="">{lookupsLoading ? "Loading shoppers..." : "Select shopper"}</option>
              {shoppers.map((shopper) => (
                <option key={shopper.id} value={shopper.email}>
                  {`${shopper.first_name} ${shopper.last_name}`.trim() || shopper.email} - {shopper.email}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-caps mb-1.5 block">Scheduled Date</label>
            <input
              type="date"
              value={manualForm.scheduled_date}
              onChange={(event) => setManualForm({ ...manualForm, scheduled_date: event.target.value })}
              className="input-shell w-full"
              required
            />
          </div>
          <div>
            <label className="label-caps mb-1.5 block">Call Type</label>
            <select
              value={manualForm.call_type}
              onChange={(event) => setManualForm({ ...manualForm, call_type: event.target.value })}
              className="select-shell w-full"
              required
            >
              <option value="">{lookupsLoading ? "Loading call types..." : "Select type"}</option>
              {callTypes.map((callType) => (
                <option key={callType.id} value={callType.name}>{callType.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-caps mb-1.5 block">Service Level</label>
            <select
              value={manualForm.service_level}
              onChange={(event) => setManualForm({ ...manualForm, service_level: event.target.value })}
              className="select-shell w-full"
              required
            >
              <option value="">{lookupsLoading ? "Loading service levels..." : "Select service"}</option>
              {serviceLevels.map((serviceLevel) => {
                const label = serviceLevelLabel(serviceLevel);
                return <option key={serviceLevel.id} value={label}>{label}</option>;
              })}
            </select>
          </div>
          <div>
            <label className="label-caps mb-1.5 block">Earliest Date</label>
            <input
              type="date"
              value={manualForm.earliest}
              onChange={(event) => setManualForm({ ...manualForm, earliest: event.target.value })}
              className="input-shell w-full"
            />
          </div>
          <div>
            <label className="label-caps mb-1.5 block">Invoice ID</label>
            <input
              value={manualForm.invoice_id}
              onChange={(event) => setManualForm({ ...manualForm, invoice_id: event.target.value })}
              className="input-shell w-full"
              placeholder="Optional"
            />
          </div>
          <div>
            <label className="label-caps mb-1.5 block">Order ID</label>
            <input
              value={manualForm.order_id}
              onChange={(event) => setManualForm({ ...manualForm, order_id: event.target.value })}
              className="input-shell w-full"
              placeholder="Optional"
            />
          </div>
          <div>
            <label className="label-caps mb-1.5 block">Language</label>
            <input
              value={manualForm.language}
              onChange={(event) => setManualForm({ ...manualForm, language: event.target.value })}
              className="input-shell w-full"
              placeholder="Optional"
            />
          </div>
          <div className="lg:col-span-3">
            <label className="label-caps mb-1.5 block">Scenario</label>
            <textarea
              value={manualForm.scenario}
              onChange={(event) => setManualForm({ ...manualForm, scenario: event.target.value })}
              className="input-shell min-h-[96px] w-full"
              placeholder="Add the shop call scenario."
            />
          </div>
          <div className="lg:col-span-3 flex justify-end">
            <button type="submit" className="button-primary" disabled={manualSaving || lookupsLoading}>
              {manualSaving ? "Saving..." : "Save one shop call"}
            </button>
          </div>
        </form>
      </SectionCard>

      <SectionCard
        title="Upload File"
        subtitle="The importer accepts .csv, .xls, and .xlsx files. Review the validation table before committing any rows."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={handlePreview} className="button-secondary" disabled={!file || loading || importing}>
              {loading ? "Validating..." : "Validate file"}
            </button>
            <button type="button" onClick={handleImport} className="button-primary" disabled={!canImport}>
              {importing ? "Importing..." : "Import ready rows"}
            </button>
          </div>
        }
      >
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr),minmax(0,0.8fr)]">
          <div className="space-y-3">
            <input
              type="file"
              accept=".xls,.xlsx,.csv"
              onChange={(event) => {
                setFile(event.target.files?.[0] || null);
                setSummary(null);
                setSummarySource(null);
                setNotice(null);
              }}
              className="input-shell !py-3"
            />
            <div className="text-sm text-slate-500">
              Use the legacy-style columns and keep the header row intact. Existing matching calls are blocked as duplicates.
            </div>
          </div>

          <div className="rounded-[28px] border border-slate-200 bg-slate-50/70 p-4">
            <div className="text-sm font-semibold text-slate-900">Expected Columns</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {EXPECTED_COLUMNS.map((column) => (
                <span key={column} className="badge-pill tone-neutral">
                  {column}
                </span>
              ))}
            </div>
          </div>
        </div>
      </SectionCard>

      <DataTableShell
        title="Validation Results"
        subtitle="Rows marked ready can import now. Errors are blocking. Warnings are informational."
      >
        {summary && summary.rows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table-modern">
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Status</th>
                  <th>Hotel</th>
                  <th>Sales Staff</th>
                  <th>Shopper</th>
                  <th>Type / Service</th>
                  <th>Scheduled</th>
                  <th>Messages</th>
                </tr>
              </thead>
              <tbody>
                {summary.rows.map((row) => (
                  <tr key={row.row_number}>
                    <td className="font-mono text-sm font-semibold text-slate-900">{row.row_number}</td>
                    <td>
                      <div className="space-y-2">
                        <StatusPill status={row.status} />
                        {row.imported_call_id ? (
                          <div className="text-xs text-emerald-700">Call #{row.imported_call_id}</div>
                        ) : null}
                        {row.duplicate_existing_call_id ? (
                          <div className="text-xs text-amber-700">Duplicate #{row.duplicate_existing_call_id}</div>
                        ) : null}
                      </div>
                    </td>
                    <td>
                      <div className="font-semibold text-slate-900">{row.hotel_code || "No code"}</div>
                      <div className="mt-1 text-sm text-slate-600">{row.resolved_property_name || row.hotel_name || "Unresolved hotel"}</div>
                      {row.resolved_hmc_name ? (
                        <div className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-500">{row.resolved_hmc_name}</div>
                      ) : null}
                    </td>
                    <td>
                      <div className="font-medium text-slate-900">{row.resolved_sales_staff_name || "Unassigned"}</div>
                      <div className="mt-1 text-sm text-slate-600">{row.sales_staff_email || "No email"}</div>
                    </td>
                    <td>
                      <div className="font-medium text-slate-900">{row.resolved_shopper_name || "Unassigned"}</div>
                      <div className="mt-1 text-sm text-slate-600">{row.shopper_email || "No email"}</div>
                    </td>
                    <td>
                      <div className="font-medium text-slate-900">{row.call_type || "Missing type"}</div>
                      <div className="mt-1 text-sm text-slate-600">{row.service_level || "Missing service level"}</div>
                      {row.resolved_language_name ? (
                        <div className="mt-1 text-xs text-slate-500">Language: {row.resolved_language_name}</div>
                      ) : null}
                    </td>
                    <td>
                      <div className="font-medium text-slate-900">{row.scheduled_date || "Missing date"}</div>
                      {row.scenario ? <div className="mt-1 text-sm text-slate-600">{row.scenario}</div> : null}
                      {row.resolved_order_id ? (
                        <div className="mt-1 text-xs text-slate-500">Order #{row.resolved_order_id}</div>
                      ) : null}
                    </td>
                    <td>
                      <div className="space-y-2">
                        {row.errors.map((message) => (
                          <div key={`error-${row.row_number}-${message}`} className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-800">
                            {message}
                          </div>
                        ))}
                        {row.warnings.map((message) => (
                          <div key={`warning-${row.row_number}-${message}`} className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800">
                            {message}
                          </div>
                        ))}
                        {row.errors.length === 0 && row.warnings.length === 0 ? (
                          <div className="text-sm text-slate-500">No issues.</div>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            title="No validation yet"
            message="Upload a call-details file and run validation to see row-level feedback before importing."
          />
        )}
      </DataTableShell>
    </div>
  );
}
